import EmailDomainInput from '@/components/features/Employees/EmailDomainInput';
import InputError from '@/components/layout/InputError';
import {
    ResponsiveModal,
    ResponsiveModalBody,
    ResponsiveModalContent,
    ResponsiveModalDescription,
    ResponsiveModalFooter,
    ResponsiveModalHeader,
    ResponsiveModalTitle,
    ResponsiveModalTrigger,
} from '@/components/ui/responsive-modal';
import { Button } from '@/components/ui/shadcn/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/shadcn/card';
// The nested "set custom password" prompt stays a centred dialog: a one-field
// prompt raised from inside this sheet is the wrong shape for a second sheet.
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/shadcn/dialog';
import { Input } from '@/components/ui/shadcn/input';
import { Label } from '@/components/ui/shadcn/label';
import { Switch } from '@/components/ui/shadcn/switch';
import { useControllableOpen } from '@/hooks/use-controllable-open';
import { useTranslations } from '@/hooks/useTranslations';
import { useCan } from '@/lib/utils';
import { User } from '@/types';
import { router, useForm } from '@inertiajs/react';
import { Edit, Info, KeyRound, Lock, Mail, Phone, RefreshCw, User as UserIcon } from 'lucide-react';
import { type FormEventHandler, type ReactNode, useState } from 'react';
import { toast } from 'sonner';

interface EditEmployeeProps {
    user: User;
    approvedEmailDomains?: string[];
    trigger?: ReactNode;
    /** Set to drive the modal from the page — see useControllableOpen. */
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
}

interface FormData {
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    suspended?: boolean;
    [key: string]: string | boolean | undefined;
}

export default function EditEmployee({ user, approvedEmailDomains = [], trigger, open: openProp, onOpenChange }: EditEmployeeProps) {
    const { t } = useTranslations();
    const requiredMark = <span className="text-destructive ml-1">*</span>;
    const [open, setOpen] = useControllableOpen(openProp, onOpenChange);
    const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
    const { can } = useCan();
    const { data, setData, put, processing, errors } = useForm<FormData>('edit-employee', {
        first_name: user.first_name,
        last_name: user.last_name,
        email: user.email,
        phone: (user.phone as string) || '',
        suspended: user.suspended_at !== null,
    });

    const passwordForm = useForm<{ password?: string }>({
        password: '',
    });

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        put(route('employees.update', user.id), {
            onSuccess: () => {
                setOpen(false);
            },
        });
    };

    const handleGeneratePassword = () => {
        router.post(
            route('employees.reset-password', user.id),
            {},
            {
                onSuccess: () => {
                    toast.success(t('employees.forms.edit.messages.password_generated', 'Password generated and sent via email'));
                },
                onError: (errorMessages) => {
                    toast.error(errorMessages.message || t('employees.forms.edit.messages.password_reset_failed', 'Failed to reset password'));
                },
            },
        );
    };

    const handleSetCustomPassword: FormEventHandler = (e) => {
        e.preventDefault();
        passwordForm.post(route('employees.reset-password', user.id), {
            onSuccess: () => {
                toast.success(t('employees.forms.edit.messages.password_reset_sent', 'Password reset and sent via email'));
                setPasswordDialogOpen(false);
                passwordForm.reset();
            },
            onError: (errorMessages) => {
                toast.error(
                    errorMessages.password ||
                        errorMessages.message ||
                        t('employees.forms.edit.messages.password_reset_failed', 'Failed to reset password'),
                );
            },
        });
    };

    return (
        <ResponsiveModal open={open} onOpenChange={setOpen} variant="sheet">
            {trigger ? (
                <ResponsiveModalTrigger asChild>{trigger}</ResponsiveModalTrigger>
            ) : openProp === undefined ? (
                // Only when the modal owns its open state: a page driving it
                // supplies its own buttons and must not get a stray one here.
                <ResponsiveModalTrigger asChild>
                    <Button variant="default">
                        <Edit className="h-4 w-4" /> {t('employees.forms.edit.buttons.edit_employee', 'Edit employee')}
                    </Button>
                </ResponsiveModalTrigger>
            ) : null}

            <ResponsiveModalContent size="xl">
                <ResponsiveModalHeader>
                    <ResponsiveModalTitle>{t('employees.forms.edit.title', 'Edit Employee')}</ResponsiveModalTitle>
                    <ResponsiveModalDescription>
                        {t('employees.forms.edit.description', 'Update employee information and access settings.')}
                    </ResponsiveModalDescription>
                </ResponsiveModalHeader>

                <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
                    <ResponsiveModalBody className="space-y-5">
                        <Card className="rounded-md shadow-none">
                            <CardHeader className="pb-4">
                                <div className="flex items-start gap-3">
                                    <span className="bg-primary/12 text-primary inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold">
                                        1
                                    </span>
                                    <div className="space-y-1">
                                        <CardTitle>{t('employees.forms.edit.sections.main_info', 'Main information')}</CardTitle>
                                        <CardDescription>
                                            {t('employees.forms.edit.sections.main_info_desc', 'Main employee details.')}
                                        </CardDescription>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent className="space-y-5">
                                <div className="grid gap-5 md:grid-cols-2">
                                    <div className="space-y-2">
                                        <Label htmlFor="first_name">
                                            {t('employees.forms.edit.labels.first_name', 'First Name')}
                                            {requiredMark}
                                        </Label>
                                        <div className="relative">
                                            <UserIcon className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
                                            <Input
                                                id="first_name"
                                                className="pl-10"
                                                value={data.first_name}
                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setData('first_name', e.target.value)}
                                            />
                                        </div>
                                        <InputError message={errors.first_name} />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="last_name">
                                            {t('employees.forms.edit.labels.last_name', 'Last Name')}
                                            {requiredMark}
                                        </Label>
                                        <div className="relative">
                                            <UserIcon className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
                                            <Input
                                                id="last_name"
                                                className="pl-10"
                                                value={data.last_name}
                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setData('last_name', e.target.value)}
                                            />
                                        </div>
                                        <InputError message={errors.last_name} />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label>
                                        {t('employees.forms.edit.labels.email', 'Email')}
                                        {requiredMark}
                                    </Label>
                                    <div className="relative">
                                        <Mail className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 z-10 h-4 w-4 -translate-y-1/2" />
                                        <div className="[&>div>input]:pl-10">
                                            <EmailDomainInput
                                                label=""
                                                value={data.email}
                                                onChange={(value) => setData('email', value)}
                                                approvedDomains={approvedEmailDomains}
                                                error={errors.email}
                                                required
                                                disabled={processing}
                                                readOnly={true}
                                                placeholder={t('employees.forms.edit.placeholders.email', 'email')}
                                                autoComplete="email"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        <Card className="rounded-md shadow-none">
                            <CardHeader className="pb-4">
                                <div className="flex items-start gap-3">
                                    <span className="bg-primary/12 text-primary inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold">
                                        2
                                    </span>
                                    <div className="space-y-1">
                                        <CardTitle>{t('employees.forms.edit.sections.extra_info', 'Additional information')}</CardTitle>
                                        <CardDescription>
                                            {t('employees.forms.edit.sections.extra_info_desc', 'Additional contact and access settings.')}
                                        </CardDescription>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent className="space-y-5">
                                <div className="space-y-2">
                                    <Label htmlFor="phone">{t('employees.forms.edit.labels.phone', 'Phone')}</Label>
                                    <div className="relative">
                                        <Phone className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
                                        <Input
                                            id="phone"
                                            className="pl-10"
                                            value={data.phone}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setData('phone', e.target.value)}
                                        />
                                    </div>
                                    <InputError message={errors.phone} />
                                </div>

                                {can('employees.update') && (
                                    <div className="flex items-center justify-between border-t pt-4">
                                        <div className="space-y-0.5">
                                            <Label htmlFor="suspended">{t('employees.forms.edit.labels.suspend_user', 'Suspend User')}</Label>
                                            <p className="text-muted-foreground text-sm">
                                                {t(
                                                    'employees.forms.edit.hints.suspend_user',
                                                    'Suspended users cannot log in and will be signed out of all sessions.',
                                                )}
                                            </p>
                                        </div>
                                        <Switch
                                            id="suspended"
                                            checked={data.suspended}
                                            onCheckedChange={(checked) => setData('suspended', checked)}
                                            aria-label={t('employees.forms.edit.labels.suspend_user', 'Suspend User')}
                                        />
                                    </div>
                                )}
                            </CardContent>
                        </Card>

                        <Card className="rounded-md shadow-none">
                            <CardHeader className="pb-4">
                                <div className="flex items-start gap-3">
                                    <span className="bg-primary/12 text-primary inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold">
                                        3
                                    </span>
                                    <div className="space-y-1">
                                        <CardTitle>{t('employees.forms.edit.labels.password_reset', 'Password Reset')}</CardTitle>
                                        <CardDescription>
                                            {t('employees.forms.edit.password_reset_desc', 'Generate a secure password or set a custom one.')}
                                        </CardDescription>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent className="space-y-3">
                                <Button type="button" variant="outline" className="w-full justify-center gap-2" onClick={handleGeneratePassword}>
                                    <RefreshCw className="h-4 w-4" />
                                    {t('employees.forms.edit.buttons.generate_send_password', 'Generate & Send Password')}
                                </Button>
                                <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
                                    <DialogTrigger asChild>
                                        <Button type="button" variant="outline" className="w-full justify-center gap-2">
                                            <KeyRound className="h-4 w-4" />
                                            {t('employees.forms.edit.buttons.set_custom_password', 'Set Custom Password')}
                                        </Button>
                                    </DialogTrigger>
                                    <DialogContent>
                                        <DialogHeader>
                                            <DialogTitle>{t('employees.forms.edit.password_dialog.title', 'Set Custom Password')}</DialogTitle>
                                            <DialogDescription>
                                                {t(
                                                    'employees.forms.edit.password_dialog.description',
                                                    'Enter a new password for this employee. The password will be sent via email.',
                                                )}
                                            </DialogDescription>
                                        </DialogHeader>
                                        <form onSubmit={handleSetCustomPassword}>
                                            <div className="grid gap-4 py-4">
                                                <div className="grid gap-2">
                                                    <Label htmlFor="password">{t('employees.forms.edit.labels.password', 'Password')}</Label>
                                                    <div className="relative">
                                                        <Lock className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
                                                        <Input
                                                            id="password"
                                                            type="password"
                                                            className="pl-10"
                                                            required
                                                            minLength={8}
                                                            value={passwordForm.data.password}
                                                            onChange={(e) => passwordForm.setData('password', e.target.value)}
                                                            placeholder={t(
                                                                'employees.forms.edit.placeholders.password',
                                                                'Enter new password (min 8 characters)',
                                                            )}
                                                        />
                                                    </div>
                                                    <InputError message={passwordForm.errors.password} />
                                                </div>
                                            </div>
                                            <DialogFooter>
                                                <Button
                                                    type="button"
                                                    variant="outline"
                                                    onClick={() => {
                                                        setPasswordDialogOpen(false);
                                                        passwordForm.reset();
                                                    }}
                                                >
                                                    {t('common.cancel', 'Cancel')}
                                                </Button>
                                                <Button type="submit" disabled={passwordForm.processing}>
                                                    {passwordForm.processing
                                                        ? t('employees.forms.edit.buttons.resetting', 'Resetting...')
                                                        : t('employees.forms.edit.buttons.reset_password', 'Reset Password')}
                                                </Button>
                                            </DialogFooter>
                                        </form>
                                    </DialogContent>
                                </Dialog>
                            </CardContent>
                        </Card>

                        <div className="border-primary/25 bg-primary/5 flex items-start gap-3 rounded-md border p-4">
                            <Info className="text-primary mt-0.5 h-5 w-5 shrink-0" />
                            <div>
                                <p className="text-primary text-sm font-semibold">{t('common.information', 'Information')}</p>
                                <p className="text-muted-foreground text-sm">
                                    {t(
                                        'employees.forms.edit.info_text',
                                        'When password reset is used, the employee will receive updated access instructions by email.',
                                    )}
                                </p>
                            </div>
                        </div>
                    </ResponsiveModalBody>

                    <ResponsiveModalFooter>
                        <Button type="submit" disabled={processing}>
                            {t('employees.forms.edit.buttons.save_changes', 'Save Changes')}
                        </Button>
                    </ResponsiveModalFooter>
                </form>
            </ResponsiveModalContent>
        </ResponsiveModal>
    );
}
